import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/core';
import { font, color, spacing, border } from '@metro-ui/tokens/js';
import { fontStack } from '../../utils/fontStack';
import { keyDownAction } from '../../utils/keyDownAction';
import { buttonSizeToTextButtonSize, iconSizeMap } from '../utils';
export var iconClass = 'm-button__icon';
var animationEvents = [
    'animationend',
    'webkitAnimationEnd',
    'mozAnimationEnd',
    'MSAnimationEnd',
    'oAnimationEnd',
];
var handleShakeAnimation = function (ref) {
    return function (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    e) {
        if (typeof ref === 'function' || ref === null)
            return;
        var element = ref.current;
        if (!element)
            return;
        var isShaking = 'isShaking';
        animationEvents.forEach(function (event) {
            var animationListener = function () {
                element.classList.remove(isShaking);
                element.removeEventListener(event, animationListener);
            };
            element.addEventListener(event, animationListener);
        });
        element.classList.add(isShaking);
    };
};
var handleOnClick = function (ref, onClick, animateShake) {
    return function (e) {
        if (animateShake)
            handleShakeAnimation(ref)(e);
        onClick(e);
    };
};
var shaking = keyframes(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  0% {\n    transform: translateX(3px);\n  }\n  20% {\n    transform: translateX(-2.5px);\n  }\n  40% {\n    transform: translateX(1.5px);\n  }\n  60% {\n    transform: translateX(-1px);\n  }\n  80% {\n    transform: translateX(0.5px);\n  }\n  100% {\n    transform: translateX(0);\n  }\n"], ["\n  0% {\n    transform: translateX(3px);\n  }\n  20% {\n    transform: translateX(-2.5px);\n  }\n  40% {\n    transform: translateX(1.5px);\n  }\n  60% {\n    transform: translateX(-1px);\n  }\n  80% {\n    transform: translateX(0.5px);\n  }\n  100% {\n    transform: translateX(0);\n  }\n"])));
var DisabledShaker = styled.span(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  opacity: 0;\n"], ["\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  opacity: 0;\n"])));
export var ButtonBase = styled(React.forwardRef(function (_a, ref) {
    var size = _a.size, _b = _a.type, type = _b === void 0 ? 'button' : _b, isDisabled = _a.isDisabled, isBeforeValidation = _a.isBeforeValidation, isWaiting = _a.isWaiting, isBeforeValidationCallback = _a.isBeforeValidationCallback, onClick = _a.onClick, Icon = _a.Icon, href = _a.href, label = _a.label, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["size", "type", "isDisabled", "isBeforeValidation", "isWaiting", "isBeforeValidationCallback", "onClick", "Icon", "href", "label", "className", "children"]);
    React.useEffect(function () {
        if (!isBeforeValidationCallback)
            return;
        isBeforeValidationCallback(!!isBeforeValidation);
    }, [isBeforeValidation, isBeforeValidationCallback]);
    var buttonElement = ref || React.createRef();
    var isLink = href !== undefined;
    var handledOnClick = onClick && !isWaiting
        ? handleOnClick(buttonElement, onClick, isBeforeValidation)
        : undefined;
    var linkAttributes = {};
    if (isLink) {
        linkAttributes = {
            tabIndex: 0,
            onKeyDown: handledOnClick && keyDownAction(handledOnClick),
            type: undefined,
            role: 'button',
            href: href,
        };
        if (isDisabled || isWaiting) {
            linkAttributes = __assign(__assign({}, linkAttributes), { tabIndex: -1, onClick: function (e) {
                    e.preventDefault();
                }, onKeyDown: function (e) {
                    e.preventDefault();
                }, disabled: undefined, 'aria-disabled': 'true' });
        }
    }
    var renderChildren = function () {
        if (isDisabled)
            return (React.createElement(React.Fragment, null,
                Icon ? children : React.createElement("span", null, children),
                React.createElement(DisabledShaker, { onClick: handleShakeAnimation(buttonElement), onKeyDown: keyDownAction(handleShakeAnimation(buttonElement)) }, children)));
        return children;
    };
    return React.createElement(isLink ? 'a' : 'button', __assign(__assign(__assign({}, htmlAttributes), { className: className, type: type, ref: buttonElement, onClick: handledOnClick, disabled: isDisabled, 'aria-label': label }), linkAttributes), renderChildren());
}))(function (_a) {
    var _b = _a.size, size = _b === void 0 ? 'medium' : _b, isDisabled = _a.isDisabled;
    var textButtonSize = buttonSizeToTextButtonSize(size);
    var iconSize = iconSizeMap[size] + "rem";
    return css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    ", "\n    display: inline-flex;\n\n    position: relative;\n    height: auto;\n    margin: ", ";\n\n    align-items: center;\n    justify-content: center;\n\n    border: 0;\n    border-radius: ", ";\n\n    color: ", ";\n\n    font-weight: ", ";\n    text-decoration: none;\n\n    outline: none;\n    cursor: pointer;\n    user-select: none;\n\n    transition: background-color 125ms ease, box-shadow 125ms ease;\n\n    font-size: ", ";\n    line-height: ", ";\n\n    ", "\n\n    & > .", " {\n      /* Uses \"rem\" and not \"em\"! Reason: https://bugs.webkit.org/show_bug.cgi?id=199236 */\n      width: ", ";\n      height: ", ";\n    }\n  "], ["\n    ", "\n    display: inline-flex;\n\n    position: relative;\n    height: auto;\n    margin: ", ";\n\n    align-items: center;\n    justify-content: center;\n\n    border: 0;\n    border-radius: ", ";\n\n    color: ", ";\n\n    font-weight: ", ";\n    text-decoration: none;\n\n    outline: none;\n    cursor: pointer;\n    user-select: none;\n\n    transition: background-color 125ms ease, box-shadow 125ms ease;\n\n    font-size: ", ";\n    line-height: ", ";\n\n    ", "\n\n    & > .", " {\n      /* Uses \"rem\" and not \"em\"! Reason: https://bugs.webkit.org/show_bug.cgi?id=199236 */\n      width: ", ";\n      height: ", ";\n    }\n  "])), fontStack, spacing.none, border.radius.small, color.inverted, font.weight.heavy, font.button[textButtonSize].size, font.button[textButtonSize]['line-height'], isDisabled && css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      cursor: not-allowed;\n\n      &.isShaking {\n        animation: ", " 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955) both;\n      }\n    "], ["\n      cursor: not-allowed;\n\n      &.isShaking {\n        animation: ", " 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955) both;\n      }\n    "])), shaking), iconClass, iconSize, iconSize);
});
ButtonBase.propTypes = {
    size: PropTypes.oneOf(['label', 'small', 'medium', 'large', 'x-large']),
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    isDisabled: PropTypes.bool,
    isBeforeValidation: PropTypes.bool,
    isBeforeValidationCallback: PropTypes.func,
    onClick: PropTypes.func,
    Icon: PropTypes.func,
    href: PropTypes.string,
    label: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=ButtonBase.js.map